import { Map } from 'immutable';

import THUMBPRINT_ART from '../../static/images/thumbprint.png';
import { DefaultArt, DefaultArtSizes } from '../constants/Art';
import { THUMBPRINT_RADIO_MUSIC_ID } from '../constants/Music';
import { PandoraTypes } from '../constants/SourceTypes';
import {
  getDynamicImageSizes,
  getDynamicImageUrl,
  getStaticImageUrl,
} from '../lib/configuration/TunerVarUtils';
import { getViewportDimensions } from '../lib/DOMUtils';
import { getTypeFromMusicId } from '../lib/MusicUtils';
import { type IListOf } from '../types';

import { trim } from './StringUtils';

const MAX_LARGE_HEADER_RESOLUTION = 1980;
const MIN_LARGE_HEADER_RESOLUTION = 1080;
const BREAK_WIDTH_LARGE = 1680;
const defaultAlbumArtistRegExp = /^.*(album|artist)_(90|130|500|600|640|1080).*png$/;

interface IRGB {
  b: number;
  g: number;
  r: number;
}

interface IRGBA extends IRGB {
  a: number;
}

export function isDefaultAlbumArt(art: string): boolean {
  return defaultAlbumArtistRegExp.test(art);
}

/**
 * Get the default art for a given content type.
 *
 * @param {string} type - type of content we want default art for
 * @param {number} resolution - desired resolution of default image
 * @returns {string} url of the art
 */
export function getDefaultArtByType(type: string, resolution = 90): string {
  let typePath: string;
  switch (type) {
    case PandoraTypes.PLAYLIST:
      typePath = 'playlist';
      break;
    case PandoraTypes.ARTIST:
      typePath = 'artist';
      break;
    case PandoraTypes.PODCAST:
    case PandoraTypes.EPISODE:
      typePath = 'podcast';
      break;
    case PandoraTypes.SONG:
    case PandoraTypes.ALBUM:
    default:
      typePath = 'album';
  }
  // Get either requested or next largest; if request res is larger than available, get largest available
  let size = DefaultArtSizes.find(res => res >= resolution);
  if (!size) {
    size = DefaultArtSizes[DefaultArtSizes.length - 1];
  }
  // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  return DefaultArt[typePath][size];
}

export function getThumbprintArt(): string {
  return THUMBPRINT_ART;
}

export function getDefaultArtByMusicId(musicId: string, resolution = 90): string {
  let type: string = PandoraTypes.ALBUM;
  if (musicId === THUMBPRINT_RADIO_MUSIC_ID) {
    return THUMBPRINT_ART;
  }
  const entityType = getTypeFromMusicId(musicId);
  if (['artist', 'composer', 'artistStation'].includes(entityType)) {
    type = PandoraTypes.ARTIST;
  }
  // 'track', 'genre', 'advertiser' all map to ALBUM, which is the default
  return getDefaultArtByType(type, resolution);
}

export function getDefaultArt(resolution = 640): string {
  return getDefaultArtByType(PandoraTypes.ALBUM, resolution);
}

export function getArtistDefaultArt(resolution = 640): string {
  return getDefaultArtByType(PandoraTypes.ARTIST, resolution);
}

function getPlaceholder(
  entity: $TSFixMe,
  resolution: number,
  pandoraType: string | null = null,
): string {
  const type = pandoraType || entity.get('type') || PandoraTypes.ALBUM;
  const musicId = entity.get('musicId');
  if (entity.get('isThumbprint')) {
    return THUMBPRINT_ART;
  } else if (musicId) {
    return getDefaultArtByMusicId(musicId, resolution);
  }
  return getDefaultArtByType(type, resolution);
}

// Arts are named differently depending on entity, this function should
// find the mapping for any entity passed in
function getArtsMap(entity: $TSFixMe): $TSFixMe | null {
  let artsMap = null;
  if (entity.has('albumArt')) {
    artsMap = entity.get('albumArt');
  } else if (entity.has('art')) {
    artsMap = entity.get('art');
  }
  if (!artsMap || artsMap.isEmpty()) {
    return null;
  }
  return artsMap;
}

function getImageSize(sizes: IListOf<number>, resolution: number): number {
  return (
    sizes
      .filter(size => {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        return size >= resolution;
      })
      .first() || sizes.last()
  );
}

// should backend supply 1980?
function getLargeHeaderImageSizes(state: $TSFixMe): $TSFixMe {
  return getDynamicImageSizes(state).push(`${MAX_LARGE_HEADER_RESOLUTION}`);
}

function generateStaticImageUrl(
  state: $TSFixMe,
  artIdOrPath: string,
  resolution = 90,
): string | null {
  if (!state || !artIdOrPath) return null;
  const staticImageUrl = getStaticImageUrl(state);
  if (!staticImageUrl) return null;

  // artIdOrPath is an `artUrl`
  if (artIdOrPath.endsWith('.jpg')) {
    return `${staticImageUrl}${artIdOrPath}`;
  }

  // artIdOrPath is an `artId`
  const size = getImageSize(getDynamicImageSizes(state), resolution);
  return `${staticImageUrl}${artIdOrPath}_${size}W_${size}H.jpg`;
}

function generateStaticLargeImageUrl(
  state: $TSFixMe,
  artIdOrPath: string | undefined,
  resolution: number,
): string | null {
  if (!state || !artIdOrPath) return null;
  const staticImageUrl = getStaticImageUrl(state);
  if (!staticImageUrl) return null;

  if (artIdOrPath.endsWith('.jpg')) {
    return `${staticImageUrl}${artIdOrPath}`;
  }
  const size = getImageSize(getLargeHeaderImageSizes(state), resolution);
  return `${staticImageUrl}${artIdOrPath}_${size}W_${size}H.jpg`;
}

export function getIcon(entity: $TSFixMe): $TSFixMe {
  if (!entity || !entity.getIn) {
    return Map();
  }

  // if the entity has a tileIcon field (ie: voice tracks), prefer that over the icon field
  const icon = entity.get('icon', Map()) || Map();
  return entity.get('tileIcon', icon) || Map();
}

export function getArtId(entity: $TSFixMe): string {
  return getIcon(entity).get('artId');
}

export function getDominantColor(entity: $TSFixMe): string {
  return getIcon(entity).get('dominantColor');
}

function getArtUrl(entity: $TSFixMe): string {
  return getIcon(entity).get('artUrl');
}

function getThorStyleArt(state: $TSFixMe, entity: $TSFixMe, resolution: number): string | null {
  if (!state) return null;
  const staticImageUrl = getStaticImageUrl(state);
  if (!staticImageUrl) return null;
  const artId = getArtId(entity);
  if (artId) {
    return generateStaticImageUrl(state, artId, resolution);
  } else if (getArtUrl(entity)) {
    return generateStaticImageUrl(state, getArtUrl(entity), resolution);
  } else if (entity.has('thorLayers')) {
    const size = getImageSize(getDynamicImageSizes(state), resolution);
    const thorLayers = entity.get('thorLayers');
    if (thorLayers.match(/.*grid\(.+\).*/)) {
      const dynamicImageUrl = getDynamicImageUrl(state);
      return `${dynamicImageUrl}?l=${encodeURIComponent(thorLayers)}&w=${size}&h=${size}`;
    } else if (!thorLayers.match(/.*grid\(\).*/)) {
      // For curated playlist with custom art, its thorLayers value is an artId
      return generateStaticImageUrl(state, thorLayers, resolution);
    }
  }
  return null;
}

export function getHero(
  state: $TSFixMe,
  entity: $TSFixMe,
  resolution = 500,
  pandoraType: string = PandoraTypes.ALBUM,
): string {
  return (
    generateStaticImageUrl(state, entity.getIn(['heroImage', 'artId']), resolution) ||
    getPlaceholder(entity, resolution, pandoraType)
  );
}

export function getLargeHeader(state: $TSFixMe, entity: $TSFixMe): string | null {
  const { width } = getViewportDimensions();
  const resolution =
    width > BREAK_WIDTH_LARGE ? MAX_LARGE_HEADER_RESOLUTION : MIN_LARGE_HEADER_RESOLUTION;
  return generateStaticLargeImageUrl(
    state,
    entity.getIn(['largeHeaderImage', 'artId']),
    resolution,
  );
}

export function getArt(
  entity: $TSFixMe,
  resolution = 90,
  state: $TSFixMe,
  pandoraType: null | string = null,
): string {
  if (!entity) {
    const defaultArtType = pandoraType || PandoraTypes.ALBUM;
    return getDefaultArtByType(defaultArtType, resolution);
  }

  const artsMap = getArtsMap(entity);
  const placeholder = getPlaceholder(entity, resolution, pandoraType);

  if (!artsMap) {
    return getThorStyleArt(state, entity, resolution) || placeholder;
  }

  // Get either requested or next largest; if request res is larger than available, get largest available
  // @ts-expect-error TS(7006) FIXME: Parameter 'art' implicitly has an 'any' type.
  const artUsed = artsMap.filter(art => art.get('size') >= resolution).first() || artsMap.last();
  return artUsed.get('url') || placeholder;
}

export function extractRGBAstring(rgbaString: string): null | IRGBA {
  const parsed = rgbaString.substring(rgbaString.indexOf('(')).split(',');
  if (parsed.length < 3) return null;
  return {
    r: parseInt(trim(parsed[0].substring(1)), 10),
    g: parseInt(trim(parsed[1]), 10),
    b: parseInt(trim(parsed[2]), 10),
    a: parseInt(trim(parsed[3]), 10),
  };
}

// https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
// Takes a hex value (with or without #) and converts to an object with
// r, g, b properties
export function hexToRgb(hex: string): IRGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : ({} as IRGB);
}

// Utils for calculating contrast

function lightOutputResponse(color: number): number {
  if (color <= 0.04045) {
    return color / 12.92;
  }
  return Math.pow((color + 0.055) / 1.055, 2.4);
}

function getLuminance({ r, g, b }: IRGB): number {
  const lr = lightOutputResponse(r / 255);
  const lg = lightOutputResponse(g / 255);
  const lb = lightOutputResponse(b / 255);

  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

export function isRGBADark(rgbObject: IRGB): boolean {
  const luminance = getLuminance(rgbObject);
  return luminance < 0.33;
}

export function isColorDark(hexColor: string): boolean {
  if (!hexColor) {
    return true; // default colors are dark
  }

  const rgb = hexToRgb(hexColor);
  return isRGBADark(rgb);
}
